// Store and Purchase department dashboard. Tiles are sourced from GetSNPDashboardSummary
// (single aggregated row, CC-scoped server-side by RoleId/UserId) — see
// spGetSNPDashboardSummary.sql.
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ClipboardList, Truck, Copy, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import PendingDocumentsModal from './PendingDocumentsModal';
import { getSNPPendingDetails } from '../../api/PurchaseAPI/snpDashboardAPI';
import {
    fetchSNPDashboardSummary,
    selectSNPDashboardSummary,
    selectSNPDashboardLoading,
} from '../../slices/dashboardSlice/snpDashboardSlice';

const cardClass = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700';

// One stage of the Indent → PO → MRR pipeline card.
const PipelineStage = ({ step, title, icon: Icon, value, subtitle, loading, onClick, children }) => (
    <button
        onClick={onClick}
        className="flex-1 w-full text-left p-4 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50/60 dark:bg-orange-900/10 hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-md transition-all"
    >
        <div className="flex items-center gap-2 mb-2">
            <span className="p-1.5 rounded-md bg-orange-100 dark:bg-orange-900/30">
                <Icon className="w-4 h-4 text-orange-600 dark:text-orange-500" />
            </span>
            <span className="text-[11px] font-semibold tracking-wide text-orange-700 dark:text-orange-400">STEP {step}</span>
        </div>
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
        <p className="text-3xl font-bold text-orange-700 dark:text-orange-500 my-1">
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (value ?? 0)}
        </p>
        {children}
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </button>
);

// One half of the "Expiring in the next 5 days" card.
const ExpiryItem = ({ title, value, loading, onClick }) => (
    <button
        onClick={onClick}
        className="flex-1 flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
    >
        <div className="flex items-center gap-3">
            <span className="p-2 rounded-md bg-gray-100 dark:bg-gray-700">
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </span>
            <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                <p className={`text-xs ${value > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {value > 0 ? 'Expiring soon · click for details' : 'All clear'}
                </p>
            </div>
        </div>
        <span className={`text-xl font-semibold ${value > 0 ? 'text-red-600 dark:text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (value ?? 0)}
        </span>
    </button>
);

const StorePurchaseDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const summary = useSelector(selectSNPDashboardSummary);
    const loading = useSelector(selectSNPDashboardLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;

    const [detailModal, setDetailModal] = useState({ open: false, loading: false, rows: [], type: null });

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // results are restricted to the cost centers assigned to this user in UserCostCenters.
        dispatch(fetchSNPDashboardSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    // type = 'Indent' | 'MRR' | 'PO' (documents under process, with current level/role) or
    // 'SupplierPOExpiry' | 'SPPOExpiry' (open POs expiring within 5 days)
    const openDetail = async (type) => {
        setDetailModal({ open: true, loading: true, rows: [], type });
        try {
            const response = await getSNPPendingDetails({ type, roleId, userId });
            setDetailModal({ open: true, loading: false, rows: response?.Data || [], type });
        } catch (error) {
            setDetailModal({ open: true, loading: false, rows: [], type });
        }
    };

    const openItems = (summary?.IndentPendingCount || 0) + (summary?.POPendingCount || 0) + (summary?.MRRPendingCount || 0);
    const expiringCount = (summary?.SupplierPOExpiringCount || 0) + (summary?.SPPOExpiringCount || 0);
    const todayLabel = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
    // Supplier / Service provider chips render only once the summary SP returns the per-type split.
    const hasPOSplit = summary?.SupplierPOPendingCount != null && summary?.SPPOPendingCount != null;

    return (
        <div className="space-y-4">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Section header */}
            <div className="flex items-end justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400">STORE &amp; PURCHASE</p>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Procurement overview</h2>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{todayLabel}</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    {/* Procurement pipeline */}
                    <div className={cardClass}>
                        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div>
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Procurement pipeline</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Items waiting at each stage · click a stage for details</p>
                            </div>
                            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 whitespace-nowrap">
                                {loading ? '…' : `${openItems} open items`}
                            </span>
                        </div>
                        <div className="flex flex-col md:flex-row md:items-stretch gap-2 p-4">
                            <PipelineStage
                                step={1}
                                title="Indents"
                                icon={ClipboardList}
                                value={summary?.IndentPendingCount}
                                subtitle="Under process at all levels"
                                loading={loading}
                                onClick={() => openDetail('Indent')}
                            />
                            <ChevronRight className="hidden md:block self-center w-4 h-4 text-gray-400 flex-shrink-0" />
                            <PipelineStage
                                step={2}
                                title="Purchase orders"
                                icon={Copy}
                                value={summary?.POPendingCount}
                                subtitle={hasPOSplit ? null : 'Supplier + service provider POs under process'}
                                loading={loading}
                                onClick={() => openDetail('PO')}
                            >
                                {hasPOSplit && (
                                    <div className="flex flex-wrap gap-1.5">
                                        <span className="text-[11px] px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                            Supplier {summary.SupplierPOPendingCount}
                                        </span>
                                        <span className="text-[11px] px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                            Service provider {summary.SPPOPendingCount}
                                        </span>
                                    </div>
                                )}
                            </PipelineStage>
                            <ChevronRight className="hidden md:block self-center w-4 h-4 text-gray-400 flex-shrink-0" />
                            <PipelineStage
                                step={3}
                                title="Material receipts (MRR)"
                                icon={Truck}
                                value={summary?.MRRPendingCount}
                                subtitle="Vendor receipts under process"
                                loading={loading}
                                onClick={() => openDetail('MRR')}
                            />
                        </div>
                    </div>

                    {/* Expiring POs */}
                    <div className={cardClass}>
                        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                            <div>
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white">Expiring in the next 5 days</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Open POs close to their validity date</p>
                            </div>
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${expiringCount > 0
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}`}>
                                {loading ? '…' : expiringCount > 0 ? `${expiringCount} expiring` : 'Nothing expiring'}
                            </span>
                        </div>
                        <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-gray-100 dark:divide-gray-700">
                            <ExpiryItem
                                title="Supplier PO"
                                value={summary?.SupplierPOExpiringCount}
                                loading={loading}
                                onClick={() => openDetail('SupplierPOExpiry')}
                            />
                            <ExpiryItem
                                title="Service provider PO (SPPO)"
                                value={summary?.SPPOExpiringCount}
                                loading={loading}
                                onClick={() => openDetail('SPPOExpiry')}
                            />
                        </div>
                    </div>
                </div>

                {/* Quick access — shared card, used as-is */}
                <div className="lg:col-span-1">
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                    />
                </div>
            </div>

            <PendingDocumentsModal
                open={detailModal.open}
                onClose={() => setDetailModal({ open: false, loading: false, rows: [], type: null })}
                type={detailModal.type}
                loading={detailModal.loading}
                rows={detailModal.rows}
            />
        </div>
    );
};

export default StorePurchaseDashboard;
